import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Compass } from "lucide-react";

/**
 * "Your answers are saved" confirmation page.
 *
 * A1 (fix cycle 2026-09-25, Mark): copy rewritten to reflect the
 * actual product state — participants can open the briefing, then
 * generate their three Lanes of Direction on the Results workspace.
 * The prior "analysis-engine-not-built" wording was left over from
 * the M1 shipping window and is no longer true after M3+.
 *
 * A3 (fix cycle 2026-09-25, Mark): if the participant already has
 * generated direction_sets (i.e., they submitted, generated, edited,
 * then re-submitted), surface a "Your directions are ready" hint that
 * links straight to /results. Rendered ONLY when a set exists — no
 * hint at all when the user has never generated. Hint failures never
 * block the page — the primary "Open your Career Repath Assessment
 * briefing" CTA is always available.
 */
export default function AssessmentSubmitted() {
  const [hasDirections, setHasDirections] = useState(false);
  const [directionsChecked, setDirectionsChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/directions");
        if (cancelled) return;
        const sets = (data && data.direction_sets) || [];
        setHasDirections(sets.length > 0);
      } catch (_err) {
        // Silent fallback: no hint if the check fails. Page still works.
      } finally {
        if (!cancelled) setDirectionsChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="rounded-lg border border-border-subtle bg-bg-surface p-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-progress-teal/10 text-progress-teal">
              <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
            </span>
            <h1
              data-testid="submitted-title"
              className="text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
            >
              Your answers are saved
            </h1>
          </div>
          <p className="mt-6 text-base leading-relaxed text-fg-secondary">
            When you're ready, open your <span className="text-fg-primary font-medium">Career Repath Assessment
            briefing</span> — from there you can generate your three <span className="text-fg-primary font-medium">Lanes of Direction</span> on the
            Results workspace.
          </p>
          <p data-testid="submitted-honest-notice" className="mt-4 text-sm text-fg-muted">
            You can keep editing your answers any time. Later edits are flagged so
            you know what changed since the last analysis — you can always regenerate.
          </p>

          {directionsChecked && hasDirections && (
            <div
              data-testid="submitted-directions-ready-hint"
              className="mt-5 rounded-md border border-progress-teal/40 bg-progress-teal/5 p-4"
            >
              <div className="flex items-start gap-2">
                <Compass className="h-4 w-4 mt-0.5 text-progress-teal flex-shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm text-fg-primary font-medium">
                    Your directions are ready.
                  </p>
                  <p className="mt-1 text-xs text-fg-secondary">
                    You already have a generated direction set. Open the Results
                    workspace to review it — or edit your answers first if
                    something's changed. Your previous sets stay saved.
                  </p>
                  <Link to="/results">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="submitted-open-results-btn"
                      className="mt-3 border-progress-teal/40 text-progress-teal hover:bg-progress-teal/10"
                    >
                      Open Results workspace <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/briefing">
              <Button
                data-testid="submitted-briefing-link"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                Open your Career Repath Assessment briefing
                <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
              </Button>
            </Link>
            <Link to="/assessment/review">
              <Button
                variant="outline"
                data-testid="submitted-edit-link"
                className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
              >
                Continue editing my answers
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button
                variant="ghost"
                data-testid="submitted-dashboard-link"
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
              >
                Back to my dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
